import { Stack, Typography, TextField, Grid, Paper, MenuItem, Button, IconButton, CircularProgress, Tooltip } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
// import { yupResolver } from "@hookform/resolvers/yup"; // TODO: Add schema later if needed
import { useEffect, useState, useMemo } from "react";
import { toast } from 'react-hot-toast';
import axios from "axios"; // Import axios

import { CONFIG } from "../../../../../config"; // Import CONFIG

// ICONS
import EditIcon from '@mui/icons-material/Edit';
import DoneIcon from '@mui/icons-material/Done';
import { useSelector } from "react-redux";
import { getAxiosErrorMessage } from "../../../../../utility/axios-error-message";
// import CloseIcon from '@mui/icons-material/Close'; // Not used in BasicDetailsForm example for edit toggle

const API_HOST = CONFIG.modules["crm"].host; // Define API Host

const combinedStatuses = [
  // "Completed",
  "Language Barrier",
  "Call done",
  "Invalid",
  "Not in the portal",
  "Miscarriage",
  "Deceased Child",
  "Deceased Mother",
  "Deceased (Mother and Child)",
  "Duplicate Mother Id",
  "Wrong number",
  "Declined",
  "Out of service",
  "No answer",
  "Busy",
  "No network",
  "Incoming not available",
  "Call Incomplete",
  "Switched off",
  "Schedule Later",
  "Pending",
  "Child Above 2 Years"
];

const CrmRelatedPropertiesForm = ({ caseData, updateCaseData }) => {

  const { Languages } = useSelector((state) => state.user || {});

  const [updateMode, setUpdateMode] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);

  const { register, handleSubmit, formState: { errors }, setValue, watch, control, getValues } = useForm({
    // resolver: yupResolver(crmRelatedPropertiesSchema), // Add schema later
    defaultValues: {
      latestStatus: caseData?.status || "",
      // badData: caseData?.badData ?? false,
      // completed: caseData?.completed ?? false,
      clusterId: caseData?.clusterId || "", // Add clusterId default
      newPregnancy: caseData?.newPregnancy ?? false, // Add newPregnancy default
    }
  });

  const watchBadData = watch("badData");
  const watchCompleted = watch("completed");

  // Effect to handle the validation logic: completed and badData cannot both be true.
  useEffect(() => {
    // If Completed is set to true, ensure Bad Data is false.
    if (watchCompleted === true && watchBadData === true) {
      setValue('badData', false);
      // Use standard toast() for informational messages
      toast("Setting 'Bad Data' to 'No' as 'Completed' is 'Yes'.", { duration: 2000 });
    }

    // If Bad Data is set to true, ensure Completed is false.
    if (watchBadData === true && watchCompleted === true) {
      // This condition might seem redundant if the first one already ran,
      // but it handles the case where badData is changed first.
      // A more robust way might involve tracking the field that triggered the change,
      // but this covers the core logic based on the current state.
      setValue('completed', false);
      // Use standard toast() for informational messages
      toast("Setting 'Completed' to 'No' as 'Bad Data' is 'Yes'.", { duration: 2000 });
    }
    // Note: No action needed if one is false and the other becomes true, or if both are false.
  }, [watchBadData, watchCompleted, setValue]);

  // Effect to update form values if caseData changes (e.g., after an update)
  useEffect(() => {
    if (caseData) {
      setValue("latestStatus", caseData.status || "");
      // Use nullish coalescing for boolean fields, defaulting to false
      setValue("badData", caseData.badData ?? false);
      setValue("completed", caseData.completed ?? false);
      setValue("clusterId", caseData.clusterId || ""); // Update clusterId on caseData change
    }
  }, [caseData, setValue]);

  const handleUpdate = async (data) => {
    // Get current values for comparison
    const currentValues = {
      latestStatus: caseData?.status || "",
      badData: caseData?.badData ?? false,
      completed: caseData?.completed ?? false,
      clusterId: caseData?.clusterId || "",
      newPregnancy: caseData?.newPregnancy ?? false,
    }

    // Check if anything changed
    if (
      data.latestStatus === currentValues.latestStatus &&
      // data.badData === currentValues.badData &&
      // data.completed === currentValues.completed &&
      data.clusterId === currentValues.clusterId &&
      data.newPregnancy === currentValues.newPregnancy
    ) {
      toast.error("No changes to update", {
        duration: 1200,
        icon: "🔴",
        style: {
          borderRadius: "10px",
          background: "#333",
          color: "#fff",
        },
        position: "top-center",
      });
      return;
    }

    setUpdateLoading(true);
    try {
      // Prepare payload - ensure only relevant fields are sent if necessary
      // For now, sending all data collected by the form
      const payload = {
        status: data.latestStatus, // Map form field name to API field name
        // badData: data.badData,
        // completed: data.completed,
        clusterId: data.clusterId || null, // Send null if empty string
        newPregnancy: data.newPregnancy,
      };

      const response = await axios.put(`${API_HOST}/case/${caseData?.motherID}`, payload);

      if (response.status === 200) {
        toast.success("CRM Properties updated successfully", {
          duration: 1200,
          icon: "🟢",
        });

        // Update local state with the potentially updated case data from the API response
        updateCaseData(response.data.case);
        setUpdateMode(false); // Exit update mode
      } else {
        // Handle non-200 success responses if necessary
        if (!response.data.success) {
          toast.error(response.data.message, {
            duration: 1200,
            icon: "🔴",
          });
        } else {
          toast.error("Something went wrong", {
            duration: 1200,
            icon: "�",
          });
        }
      }

    } catch (error) {
      console.error("Error updating CRM properties:", error); // Log the error
      toast.error(getAxiosErrorMessage(error), {
        duration: 1200,
        icon: "🔴",
        style: {
          borderRadius: "10px",
          background: "#333",
          color: "#fff",
        },
        position: "top-center",
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  return (
    <Stack direction={"column"} spacing={1} px={2} py={1}>
      <Stack component={Paper} elevation={2} direction={"column"} spacing={1} px={2} py={2}>
        <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
          <Typography variant="h6" gutterBottom>
            CRM Related Properties
          </Typography>
          <Stack>
            <IconButton
              disabled={updateLoading}
              color="primary"
              onClick={() => setUpdateMode(!updateMode)}
            >
              {updateMode ? <DoneIcon /> : <EditIcon />}
            </IconButton>
          </Stack>
        </Stack>

        <Stack component={"form"} onSubmit={handleSubmit(handleUpdate)} direction={"column"} spacing={2}>
          <Grid container rowGap={2} columnSpacing={{ xs: 0, md: 2 }} sx={{
            pr: {
              xs: 0,
              md: 2
            }
          }}>
            <Grid item xs={12} md={4}>
              <Controller
                name="latestStatus"
                control={control}
                render={({ field }) => (
                  <TextField
                    select
                    fullWidth
                    label="Latest Status"
                    {...field}
                    error={!!errors.latestStatus}
                    helperText={errors.latestStatus?.message}
                    disabled={!updateMode}
                  >
                    {combinedStatuses.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            {/* <Grid item xs={12} md={4}>
              <Controller
                name="badData"
                control={control}
                render={({ field }) => (
                  <TextField
                    select
                    fullWidth
                    label="Bad Data"
                    {...field}
                    error={!!errors.badData}
                    helperText={errors.badData?.message}
                    disabled={!updateMode}
                  >
                    <MenuItem key="badData-yes" value={true}>Yes</MenuItem>
                    <MenuItem key="badData-no" value={false}>No</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="completed"
                control={control}
                render={({ field }) => (
                  <TextField
                    select
                    fullWidth
                    label="Completed"
                    {...field}
                    error={!!errors.completed}
                    helperText={errors.completed?.message}
                    disabled={!updateMode}
                  >
                    <MenuItem key="completed-yes" value={true}>Yes</MenuItem>
                    <MenuItem key="completed-no" value={false}>No</MenuItem>
                  </TextField>
                )}
              />
            </Grid> */}
            {/* Cluster Dropdown */}
            <Grid item xs={12} md={4}>
              <Controller
                name="clusterId"
                control={control}
                render={({ field }) => (
                  <TextField
                    select
                    fullWidth
                    label="Cluster"
                    {...field}
                    error={!!errors.clusterId} // Add error handling if needed later
                    helperText={errors.clusterId?.message} // Add helper text if needed later
                    disabled={!updateMode}
                  >
                    <MenuItem value="">
                      <em>None</em> {/* Optional: Add a 'None' option */}
                    </MenuItem>
                    {(Languages || []).map((lang) => (
                      <MenuItem key={lang._id} value={lang._id}>
                        {lang.name} {/* Assuming Language object has a 'name' property */}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            {/* New Pregnancy Dropdown - Conditionally Rendered */}
            {(caseData?.badData || caseData?.completed) && (
              <Grid item xs={12} md={4}>
                <Controller
                  name="newPregnancy"
                  control={control}
                  render={({ field }) => (
                    <Tooltip title="This option appears only if the case is 'Completed' or marked as 'Bad Data'. Setting to 'Yes' will create a new case for assignment." arrow>
                      {/* Wrap the TextField with a span or div for Tooltip to attach correctly when disabled */}
                      <span>
                        <TextField
                          select
                          fullWidth
                          label="New Pregnancy"
                          {...field}
                          error={!!errors.newPregnancy}
                          helperText={errors.newPregnancy?.message}
                          disabled={!updateMode}
                          sx={{ width: '100%' }} // Ensure TextField takes full width of the span
                        >
                          <MenuItem key="newPregnancy-yes" value={true}>Yes</MenuItem>
                          <MenuItem key="newPregnancy-no" value={false}>No</MenuItem>
                        </TextField>
                      </span>
                    </Tooltip>
                  )}
                />
              </Grid>
            )} {/* Close conditional rendering */}
          </Grid>
          {updateMode && (
            <Stack direction={"row"} justifyContent={"flex-end"}>
              <Button type="submit" variant="contained" color="primary" disabled={updateLoading} sx={{ height: "100%" }}>
                {updateLoading ? <CircularProgress size={20} /> : "Update"}
              </Button>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default CrmRelatedPropertiesForm;
